import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { productMasterService } from "@/services/api/productMaster";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";

interface loadingState {
    masterProduct:boolean,
    productReport:boolean,
}
interface IProductMaster {
  masterProducts: {
    Items?: any[];
    TotalRecords?: number;
  };
  productsReport: { Items?: any[]; TotalCount?: number };
  loading: loadingState;
}

export const fetchProductInventory = createAsyncThunk(
  "productMaster/fetchProductInventory",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response =
        await productMasterService.fetchProductInventory(payload);
      return handleResponse(response, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch Product Inventory data",
      );
    }
  },
);

export const updateProduct = createAsyncThunk(
  "productMaster/updateProduct",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await productMasterService.updateProduct(payload);
      const result = handleResponse(res, rejectWithValue);
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const updateChildProduct = createAsyncThunk(
  "productMaster/updateChildProduct",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await productMasterService.updateChildProduct(payload);
      const result = handleResponse(res, rejectWithValue);
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const addProduct = createAsyncThunk(
  "productMaster/addProduct",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await productMasterService.addProduct(payload);
      const result = handleResponse(res, rejectWithValue);
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const addChildProduct = createAsyncThunk(
  "productMaster/addChildProduct",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await productMasterService.addChildProduct(payload);
      const result = handleResponse(res, rejectWithValue);
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const fetchMasterProducts = createAsyncThunk(
  "productMaster/fetchMasterProducts",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await productMasterService.fetchMasterProducts(payload);
      return handleResponse(response, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch master products data",
      );
    }
  },
);

export const fetchChildMasterProducts = createAsyncThunk(
  "productMaster/fetchMasterProducts",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response =
        await productMasterService.fetchChildMasterProducts(payload);
      return handleResponse(response, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch child products data",
      );
    }
  },
);

export const fetchProduct = createAsyncThunk(
  "productMaster/fetchProduct",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await productMasterService.fetchProduct(payload);
      return handleResponse(response, rejectWithValue);
    } catch (error: any) {
      return rejectWithValue(error?.Message ?? "Failed to fetch product data");
    }
  },
);

export const deleteProduct = createAsyncThunk(
  "productMaster/deleteProduct",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await productMasterService.deleteProduct(id);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to delete Product");
    }
  },
);

export const deleteChildProduct = createAsyncThunk(
  "productMaster/deleteChildProduct",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await productMasterService.deleteChildProduct(id);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to delete Child Product");
    }
  },
);

export const updateProductInventory = createAsyncThunk(
  "productMaster/updateProductInventory",
  async (payload: any, { rejectWithValue }) => {
    try {
      const result = await productMasterService.updateProductInventory(payload);
      return result;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const productBulkUpload = createAsyncThunk(
  "productMaster/productBulkUpload",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await productMasterService.productBulkUpload(payload);
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const downloadAllProducts = createAsyncThunk(
  "productMaster/downloadAllProducts",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await productMasterService.downloadAllProducts(payload);
      return saveAsExcelFile(response, "Products");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to download All Products",
      );
    }
  },
);

export const fetchProductsReport = createAsyncThunk(
  "productMaster/fetchProductsReport",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await productMasterService.fetchProductsReport(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch products report data",
      );
    }
  },
);

export const fetchBulkUploadViewReports = createAsyncThunk(
  "productMaster/fetchBulkUploadViewReports",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response =
        await productMasterService.fetchBulkUploadViewReports(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch bulk upload view reports data",
      );
    }
  },
);

export const exportBulkUploadReport = createAsyncThunk(
  "productMaster/exportBulkUploadReport",
  async (fileId: string, { rejectWithValue }) => {
    try {
      const response =
        await productMasterService.exportBulkUploadReport(fileId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to export bulk upload report",
      );
    }
  },
);

export const exportBulkUploadFile = createAsyncThunk(
  "productMaster/exportBulkUploadFile",
  async (fileId: string, { rejectWithValue }) => {
    try {
      const response = await productMasterService.exportBulkUploadFile(fileId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to export bulk upload file",
      );
    }
  },
);

export const productBulkUpdate = createAsyncThunk(
  "productMaster/productBulkUpdate",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await productMasterService.productBulkUpdate(payload);
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);



const initialState: IProductMaster = {
  masterProducts: {
    Items: [],
    TotalRecords: 0,
  },
  productsReport: {},
  
  loading: {
    masterProduct: false,
    productReport: false,
  },
};

const productMasterSlice = createSlice({
  name: "productMaster",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterProducts.pending, (state) => {
        state.loading.masterProduct = true;
      })
      .addCase(fetchMasterProducts.fulfilled, (state, action) => {
        state.loading.masterProduct = false;
        state.masterProducts = action.payload;
      })
      .addCase(fetchMasterProducts.rejected, (state, action) => {
        state.loading.masterProduct = false;
      })
      .addCase(fetchProductsReport.pending, (state) => {
        state.loading.productReport = true;
      })
      .addCase(fetchProductsReport.fulfilled, (state, action) => {
        state.productsReport = action.payload;
        state.loading.productReport = false;
      })
      .addCase(fetchProductsReport.rejected, (state, action) => {
        state.loading.productReport = false;
      });
    
  },
});

export const {} = productMasterSlice.actions;
export default productMasterSlice.reducer;
